import { NavigableButton } from '@carvana/ds-button';
import Link from '@carvana/ds-link';
import { TransactionIntent } from '@carvana/merch-contracts';
import { DispatcherEvent } from '@carvana/merch-native';
import cx from 'classnames';
import React, { memo, useCallback, useContext, useState } from 'react';
import StatusBadge from '@/common/helios/TradeCard/StatusBadge';
import TrackWrapper from '@/common/TrackWrapper';
import { TradeStatus, getTradeCtaDetails } from '@/common/TradeCard/constants';
import COOKIES from '~/constants/cookies';
import Sections from '~/constants/home/sections';
import TradeContext, { useIsTradeLoaded } from '~/context/common/TradeContext';
import ModalContext from '~/context/home/ModalContext';
import { useGetUserPurchaseIsLoaded } from '~/context/home/UserPurchaseContext';
import useRefreshCtaClick from '~/hooks/common/appraisal/useRefreshCtaClick';
import useTradeCtaClick from '~/hooks/common/appraisal/useTradeCtaClick';
import useGetTradeInStatus from '~/hooks/common/selectors/useGetTradeStatus';
import { nativeStcPayload } from '~/native/common/utils';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import BrowserCookies from '~/utilities/BrowserCookies';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import formatEvent from '~/utilities/formatEvent';
import { CardType, fireAction } from './actionNames';
import DefaultTradeCard from './DefaultTradeCard';
import MultiTradeSelect from './MultiTradeSelect';
import {
  OfferCardContainer,
  OfferCardContent,
  HeadingSection,
  OfferHeading
} from './SharedCard.styles';
import {
  VehicleTileContainer,
  VehicleImageContainer,
  VehicleDetailsContainer,
  VehicleName,
  VehiclePrice
} from './TradeCard.styles';
import TradeLoadingCard from './TradeLoadingCard';
import TradeVehicleImage from './TradeVehicleImage';

function TradeCard() {
  const { selectedTrade } = useContext(TradeContext);
  const {
    setAppraisalEditAcquisitionName,
    setAppraisalEmbeddedModalOpen,
    setAppraisalRefreshModalOpen
  } = useContext(ModalContext);
  const openCardEmbeddedModal = useCallback(
    (isOpen: boolean) => setAppraisalEmbeddedModalOpen(isOpen, 'home-card'),
    [setAppraisalEmbeddedModalOpen]
  );
  const { isEligible: isTradeCtaEligible, onClick: handleTradeCtaClick } =
    useTradeCtaClick(openCardEmbeddedModal);
  const handleRefreshCtaClick = useRefreshCtaClick({
    isEligible: isTradeCtaEligible,
    setAppraisalEditAcquisitionName,
    setAppraisalEmbeddedModalOpen: openCardEmbeddedModal,
    setAppraisalRefreshModalOpen
  });
  const {
    transactionIntent = TransactionIntent.Unknown,
    daysToExpiration = 0,
    id = '',
    offerAmount = 0,
    vehicle: { make = '', model = '', year = 0 } = {}
  } = selectedTrade ?? {};

  const tradeStatus = useGetTradeInStatus();
  const hasTrade = tradeStatus !== TradeStatus.None;
  const [isMultiTradeSelectOpen, setIsMultiTradeSelectOpen] = useState(false);

  const { ctaText, ctaHref, nativePayload } = getTradeCtaDetails(
    tradeStatus,
    transactionIntent,
    id
  );

  const commonCustomAttributes = {
    description: 'STC card',
    expireDays: daysToExpiration,
    status: tradeStatus === TradeStatus.None ? 'default' : tradeStatus,
    cta: ctaText
  };

  const buttonVariant =
    tradeStatus === TradeStatus.Expired ? 'secondary' : 'primary';

  const showFullDetails = [
    TradeStatus.Active,
    TradeStatus.ExpiringSoon
  ].includes(tradeStatus);

  const handleCtaClick = (
    ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    fireAction(CardType.Trade);

    if (tradeStatus === TradeStatus.Expired) {
      handleRefreshCtaClick(ev);
      if (ev.defaultPrevented) return;
    }

    if (tradeStatus === TradeStatus.None) {
      handleTradeCtaClick(ev);
      if (ev.defaultPrevented) return;
    }

    BrowserCookies.remove(COOKIES.tradeInApplied);
    sendMessageToNative(DispatcherEvent.ToggleTradeIn, {
      isTradeInApplied: true
    });
  };

  const vehicleName = year && make && model ? `${year} ${make} ${model}` : '';

  const formattedPrice = offerAmount
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(offerAmount)
    : '';

  if (!hasTrade) {
    return (
      <DefaultTradeCard
        ctaHref={ctaHref}
        dataNativeIncluded={isTradeCtaEligible ? false : undefined}
        dataNativePayload={isTradeCtaEligible ? undefined : nativePayload}
        data-testid="default-trade-card"
        onClick={handleCtaClick}
      />
    );
  }

  const vehicleNameElement = (
    <VehicleName
      className={cx(
        showFullDetails
          ? 'line-clamp-1 t-heading-xs'
          : 'line-clamp-2 t-heading-sm'
      )}
      data-testid="trade-vehicle-name"
    >
      {vehicleName}
    </VehicleName>
  );

  return (
    <TrackWrapper
      event={formatEvent('Home feed card', {
        action: 'viewed',
        ...commonCustomAttributes
      })}
      trackOnce
      key={tradeStatus}
    >
      <OfferCardContainer>
        <OfferCardContent>
          <HeadingSection>
            <OfferHeading>Trade offer</OfferHeading>
            <StatusBadge />
          </HeadingSection>

          <VehicleTileContainer>
            <VehicleImageContainer>
              <TradeVehicleImage />
            </VehicleImageContainer>

            <VehicleDetailsContainer className="justify-between">
              {showFullDetails ? (
                <>
                  {vehicleNameElement}

                  <div className="flex items-end gap-md">
                    <VehiclePrice data-testid="trade-vehicle-price">
                      {formattedPrice}
                    </VehiclePrice>
                    <Link
                      href={`/sell-my-car/hub/${id}`}
                      data-native-payload={nativeStcPayload}
                      data-analytics-id="Home feed card"
                      data-analytics-attributes={JSON.stringify({
                        description: 'STC card',
                        expireDays: daysToExpiration,
                        status: tradeStatus,
                        item: 'view offer'
                      })}
                      data-testid="trade-view-offer"
                      preventVisitedColor
                      size="sm"
                      variant="standalone"
                    >
                      View Offer
                    </Link>
                  </div>

                  <div className="relative">
                    <MultiTradeSelect
                      isOpen={isMultiTradeSelectOpen}
                      setIsOpen={setIsMultiTradeSelectOpen}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center">{vehicleNameElement}</div>

                  <div className="relative">
                    <MultiTradeSelect
                      isOpen={isMultiTradeSelectOpen}
                      setIsOpen={setIsMultiTradeSelectOpen}
                    />
                  </div>
                </>
              )}
            </VehicleDetailsContainer>
          </VehicleTileContainer>

          <NavigableButton
            href={ctaHref}
            data-native-payload={nativePayload}
            data-analytics-id="Home feed card"
            data-analytics-attributes={JSON.stringify({
              ...commonCustomAttributes,
              item: 'cta button'
            })}
            data-testid="trade-card-cta"
            onClick={handleCtaClick}
            size="sm"
            variant={buttonVariant}
            className="w-full capitalize"
          >
            {ctaText}
          </NavigableButton>
        </OfferCardContent>
      </OfferCardContainer>
    </TrackWrapper>
  );
}

function TradeCardWrapper() {
  const isUserPurchaseLoaded = useGetUserPurchaseIsLoaded();
  const isTradesLoaded = useIsTradeLoaded();

  return isTradesLoaded && isUserPurchaseLoaded ? (
    <TradeCard />
  ) : (
    <TradeLoadingCard />
  );
}

export default withErrorBoundary(memo(TradeCardWrapper), {
  section: Sections.TradeCard
});
